<template>
  <div class="purchase-packs">
    <PurchasePackCard
      v-for="product in products"
      :key="product.id"
      :amount="product.formatted"
      :reference="product.reference"
      :extra="promo"
      :highlighted="selectedPackId === product.id"
      @selectPack="selectPack(product)"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import PurchasePackCard from '@/components/PurchasePackCard.vue';
import { PurchasePack, PurchasePacks } from '@/types/products';
import { useMixpanel } from '@/composables';
import { TrackEvents } from '@/types/trackEvents';

const formatCents = (currency: string) => (cents: number) => {
  const locale = navigator.language;
  return new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
};

export default defineComponent({
  name: 'PurchasePacks',
  components: { PurchasePackCard },
  props: {
    packs: {
      type: Object as PropType<PurchasePacks>,
      required: false,
      default: () => ({
        currency: 'USD',
        products: [],
      }),
    },
    code: {
      type: String,
      required: false,
      default: '',
    },
    selectedPackId: {
      type: Number as PropType<number | null>,
      required: false,
      default: null,
    },
    extra: {
      type: [Object, null] as PropType<{ amount: number | null; percentage: number | null } | null>,
      required: false,
      default: () => ({ amount: null, percentage: null }),
    },
  },
  emits: ['update:code', 'update:selectedPackId'],
  setup(props, { emit }) {
    const { track } = useMixpanel();
    const value = ref(props.code);

    const formatMoney = formatCents(props.packs?.currency);

    const promo = computed(() => {
      const amount = props.extra?.amount ?? null;
      const percentage = props.extra?.percentage ?? null;
      return amount !== null ? formatMoney(amount) : percentage !== null ? `${percentage}%` : null;
    });

    const products = computed(() => {
      return props.packs.products
        .map((product: PurchasePack) => ({
          ...product,
          formatted: formatMoney(product.amount),
        }))
        .slice(0, 3);
    });

    onMounted(() => {
      const pack = products.value.find((product) => props.selectedPackId === product.id);
      if (pack) selectPack(pack);
    });

    const selectPack = (pack: PurchasePack) => {
      track(TrackEvents.PACKAGE_SELECTION, {
        package_id: pack.id,
        package_amount: pack.amount,
        currency: props.packs?.currency,
      });
      emit('update:selectedPackId', pack.id);
    };

    return {
      value,
      products,
      selectPack,
      promo,
    };
  },
});
</script>

<style scoped lang="scss">
.expand-toggle {
  color: $color-primary;
}

.purchase-packs {
  display: flex;
  gap: 0.5rem;
}

.purchase-pack-item {
  @extend %primary-border;
  display: flex;
  align-items: center;
  padding: 0 16px;
  height: 60px;
  font-size: 18px;
  margin-bottom: 16px;

  &__left {
    display: flex;
    flex-direction: column;
  }

  &:hover {
    background-color: $color-hover-bg;
  }
}

.purchase-pack__highlight {
  position: relative;
  top: -12px;
  font-size: 14px;
}

.promo {
  @extend %primary-border;
  border-color: $color-primary;
  padding: 4px 12px;
  color: $color-primary;
  margin-left: auto;
}

.highlight {
  margin-top: 4px;
  font-size: 12px;
}

.price,
.promo,
.highlight {
  font-weight: bold;
}
</style>
